import Dialog from "../../components/General/ConfirmableDialog";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs, { CHICAGO_TIMEZONE } from "../../utils/dayjsTimezone";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import CompleteMaintenanceOperationButton from "../../components/Fleet/CompleteMaintenanceOperationButton/CompleteMaintenanceOperationButton";
import MaintenanceTable from "../../components/Fleet/MaintenanceTable/MaintenanceTable";

interface TruckListItem {
  id: string;
  unitNumber: string;
  licencePlate?: string;
  vin?: string;
}

type YardRecallScheduleStatus = "unconfirmed" | "confirmed";

interface YardRecallScheduleViewDto {
  id: string;
  truckId: string;
  truckUnitNumber: string;
  scheduledTime: string;
  status: YardRecallScheduleStatus;
  driverId?: string | null;
  driverName?: string | null;
  driverPhoneNumber?: string | null;
  trailerId?: string | null;
  trailerUnitNumber?: string | null;
  maintenanceOperationId?: string | null;
}

interface CreateSchedulePayload {
  truckId: string;
  scheduledTime: string;
  status: YardRecallScheduleStatus;
}

interface UpdateSchedulePayload {
  id: string;
  scheduledTime: string;
  status: YardRecallScheduleStatus;
  updateTime: boolean;
  updateStatus: boolean;
}

const STATUS_OPTIONS: Array<{
  value: YardRecallScheduleStatus;
  label: string;
}> = [
  { value: "unconfirmed", label: "Unconfirmed" },
  { value: "confirmed", label: "Confirmed" },
];

const buildCalendar = (month: Dayjs) => {
  const startOfMonth = month.startOf("month");
  const endOfMonth = month.endOf("month");
  const daysInMonth = endOfMonth.date();
  const startWeekday = startOfMonth.day();

  const cells: Array<Dayjs | null> = [];

  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(startOfMonth.date(day));
  }

  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let i = remainder; i < 7; i += 1) {
      cells.push(null);
    }
  }

  return cells;
};

const WEEKDAY_LABELS = [
  { key: "sun", label: "S" },
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
];

const STATUS_LABEL_MAP = STATUS_OPTIONS.reduce(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {} as Record<YardRecallScheduleStatus, string>,
);

const fetchAllTrucks = async (
  token: string | null | undefined,
): Promise<TruckListItem[]> => {
  const headers = { Authorization: `Bearer ${token}` };

  const [activeResponse, inactiveResponse] = await Promise.all([
    axios.get(`${apiVariable}/getAllTrucks`, {
      headers,
      params: { areActive: true },
    }),
    axios.get(`${apiVariable}/getAllTrucks`, {
      headers,
      params: { areActive: false },
    }),
  ]);

  const map = new Map<string, TruckListItem>();

  [...activeResponse.data, ...inactiveResponse.data].forEach((truck: any) => {
    if (!truck?.id) {
      return;
    }
    map.set(truck.id, {
      id: truck.id,
      unitNumber: truck.unitNumber ?? "Unknown",
      licencePlate: truck.licencePlate ?? undefined,
      vin: truck.vin ?? undefined,
    });
  });

  return Array.from(map.values()).sort((a, b) =>
    a.unitNumber.localeCompare(b.unitNumber, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
};

const fetchSchedules = async (
  token: string | null | undefined,
): Promise<YardRecallScheduleViewDto[]> => {
  const { data } = await axios.get(`${apiVariable}/yardRecallSchedules`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const resolveErrorMessage = (error: unknown) => {
  if (!error) {
    return undefined;
  }
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData === "string") {
      return responseData;
    }
    if (responseData && typeof responseData === "object") {
      if (
        "message" in responseData &&
        typeof responseData.message === "string"
      ) {
        return responseData.message;
      }
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return undefined;
};

const YardRecallSchedulePage = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const initialMonth = dayjs().tz(CHICAGO_TIMEZONE).startOf("month");
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(initialMonth);
  const [selectedDay, setSelectedDay] = useState<Dayjs | null>(null);
  const [truckSearch, setTruckSearch] = useState("");
  const [selectedTruck, setSelectedTruck] = useState<TruckListItem | null>(
    null,
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] =
    useState<YardRecallScheduleViewDto | null>(null);
  const [scheduleToDelete, setScheduleToDelete] =
    useState<YardRecallScheduleViewDto | null>(null);

  const {
    data: trucks = [],
    isLoading: isLoadingTrucks,
    isError: isErrorTrucks,
    error: trucksError,
  } = useQuery({
    queryKey: ["yardRecallTrucks"],
    queryFn: () => fetchAllTrucks(token),
  });

  const {
    data: schedules = [],
    isLoading: isLoadingSchedules,
    isError: isErrorSchedules,
    error: schedulesError,
  } = useQuery({
    queryKey: ["yardRecallSchedules"],
    queryFn: () => fetchSchedules(token),
  });

  const createScheduleMutation = useMutation({
    mutationFn: (payload: CreateSchedulePayload) =>
      axios.post(`${apiVariable}/yardRecallSchedules`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["yardRecallSchedules"] });
      setCreateDialogOpen(false);
      setSelectedTruck(null);
    },
  });

  const updateScheduleMutation = useMutation({
    mutationFn: async (payload: UpdateSchedulePayload) => {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const body = {
        scheduledTime: payload.scheduledTime,
        status: payload.status,
      };

      await axios.patch(
        `${apiVariable}/yardRecallSchedules/${payload.id}/scheduledTime`,
        JSON.stringify(body),
        { headers },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["yardRecallSchedules"] });
      setEditDialogOpen(false);
      setScheduleToEdit(null);
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      axios.delete(`${apiVariable}/yardRecallSchedules/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["yardRecallSchedules"] });
      setScheduleToDelete(null);
    },
  });

  const scheduledWithDates = useMemo(
    () =>
      schedules
        .filter((schedule) => schedule.scheduledTime)
        .map((schedule) => ({
          schedule,
          scheduledDate: dayjs.utc(schedule.scheduledTime).tz(CHICAGO_TIMEZONE),
        })),
    [schedules],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map<
      string,
      { date: Dayjs; items: YardRecallScheduleViewDto[] }
    >();
    scheduledWithDates.forEach(({ schedule, scheduledDate }) => {
      if (!scheduledDate.isValid()) {
        return;
      }

      const key = scheduledDate.format("YYYY-MM-DD");
      const existing = map.get(key);

      const updatedSchedule = {
        ...schedule,
        scheduledTime: scheduledDate.toISOString(),
      };

      if (existing) {
        existing.items.push(updatedSchedule);
        existing.items.sort((a, b) =>
          dayjs(a.scheduledTime).isAfter(dayjs(b.scheduledTime)) ? 1 : -1,
        );
      } else {
        map.set(key, {
          date: scheduledDate.startOf("day"),
          items: [updatedSchedule],
        });
      }
    });
    return map;
  }, [scheduledWithDates]);

  const calendarCells = useMemo(
    () => buildCalendar(currentMonth),
    [currentMonth],
  );

  useEffect(() => {
    if (selectedDay) {
      return;
    }
    const firstMatch = scheduledWithDates.find(({ scheduledDate }) =>
      scheduledDate.isSame(currentMonth, "month"),
    );
    if (firstMatch) {
      setSelectedDay(firstMatch.scheduledDate.startOf("day"));
    }
  }, [currentMonth, scheduledWithDates, selectedDay]);

  const selectedKey = selectedDay?.format("YYYY-MM-DD") ?? "";
  const selectedItems = selectedKey
    ? (eventsByDay.get(selectedKey)?.items ?? [])
    : [];

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: CHICAGO_TIMEZONE,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    [],
  );

  const filteredTrucks = useMemo(() => {
    const search = truckSearch.trim().toLowerCase();
    if (!search) {
      return trucks;
    }
    return trucks.filter((truck) =>
      truck.unitNumber.toLowerCase().includes(search),
    );
  }, [truckSearch, trucks]);

  const totalForMonth = useMemo(
    () =>
      scheduledWithDates.filter(({ scheduledDate }) =>
        scheduledDate.isSame(currentMonth, "month"),
      ).length,
    [scheduledWithDates, currentMonth],
  );

  const statusCounts = useMemo(() => {
    const counts: Record<YardRecallScheduleStatus, number> = {
      unconfirmed: 0,
      confirmed: 0,
    };

    scheduledWithDates.forEach(({ schedule, scheduledDate }) => {
      if (scheduledDate.isSame(currentMonth, "month")) {
        counts[schedule.status] = (counts[schedule.status] ?? 0) + 1;
      }
    });

    return counts;
  }, [scheduledWithDates, currentMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
    setSelectedDay(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, "month"));
    setSelectedDay(null);
  };

  const handleDateChange = (value: Dayjs | null) => {
    if (value) {
      setCurrentMonth(value.startOf("month"));
      setSelectedDay(null);
    }
  };

  const handleOpenCreateDialog = (truck: TruckListItem) => {
    setSelectedTruck(truck);
    setCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (schedule: YardRecallScheduleViewDto) => {
    setScheduleToEdit(schedule);
    setEditDialogOpen(true);
  };

  const handleCreateSubmit = (value: Dayjs | null) => {
    if (!value || !selectedTruck) {
      return;
    }

    const payload: CreateSchedulePayload = {
      truckId: selectedTruck.id,
      scheduledTime: value.tz(CHICAGO_TIMEZONE, true).utc().toISOString(),
      status: "unconfirmed",
    };

    createScheduleMutation.mutate(payload);
  };

  const handleUpdateSubmit = (
    value: Dayjs | null,
    status: YardRecallScheduleStatus,
  ) => {
    if (!value || !scheduleToEdit) {
      return;
    }

    const scheduledTime = value.tz(CHICAGO_TIMEZONE, true).utc().toISOString();
    const updateTime = scheduledTime !== scheduleToEdit.scheduledTime;
    // Also send status whenever time changes, even if the status value is the same
    const updateStatus = updateTime || status !== scheduleToEdit.status;

    if (!updateTime && !updateStatus) {
      setEditDialogOpen(false);
      setScheduleToEdit(null);
      return;
    }

    const payload: UpdateSchedulePayload = {
      id: scheduleToEdit.id,
      scheduledTime,
      status,
      updateTime,
      updateStatus,
    };

    updateScheduleMutation.mutate(payload);
  };

  if (isLoadingTrucks || isLoadingSchedules) {
    return (
      <Box
        sx={{
          flexWrap: { xs: "wrap", lg: "nowrap" },
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isErrorTrucks || isErrorSchedules) {
    const errorMessage =
      resolveErrorMessage(trucksError) ??
      resolveErrorMessage(schedulesError) ??
      "Failed to load data.";
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Stack spacing={2}>
              <Typography variant="h6">Trucks</Typography>
              <TextField
                label="Search by Unit Number"
                value={truckSearch}
                onChange={(event) => setTruckSearch(event.target.value)}
                fullWidth
              />
              <Divider />
              {filteredTrucks.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No trucks found.
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 500, overflowY: "auto" }}>
                  {filteredTrucks.map((truck) => (
                    <ListItem key={truck.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleOpenCreateDialog(truck)}
                      >
                        <ListItemText
                          primary={truck.unitNumber}
                          secondary={
                            truck.licencePlate || truck.vin
                              ? [truck.licencePlate, truck.vin]
                                  .filter(Boolean)
                                  .join(" • ")
                              : undefined
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
              <Typography variant="caption" color="textSecondary">
                Click a truck to schedule a yard recall.
              </Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          <Stack spacing={3}>
            <Paper sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack
                  flexWrap={{ xs: "wrap", lg: "nowrap" }}
                  useFlexGap
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  <Button variant="outlined" onClick={handlePrevMonth}>
                    Previous
                  </Button>
                  <Typography variant="h5">
                    {currentMonth.format("MMMM YYYY")}
                  </Typography>
                  <Button variant="outlined" onClick={handleNextMonth}>
                    Next
                  </Button>
                </Stack>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["year", "month"]}
                    label="Jump to"
                    value={currentMonth}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Grid container columns={7} spacing={1} mb={1}>
                {WEEKDAY_LABELS.map(({ key, label }) => (
                  <Grid item xs={1} key={key}>
                    <Typography variant="subtitle2" align="center">
                      {label}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid container columns={7} spacing={1}>
                {calendarCells.map((cell, index) => {
                  if (!cell) {
                    return <Grid item xs={1} key={`empty-${index}`} />;
                  }
                  const key = cell.format("YYYY-MM-DD");
                  const itemsForDay = eventsByDay.get(key)?.items ?? [];
                  const isSelected = selectedDay?.isSame(cell, "day");
                  return (
                    <Grid item xs={1} key={key}>
                      <Paper
                        variant={isSelected ? "outlined" : undefined}
                        sx={{
                          p: 1,
                          cursor: "pointer",
                          backgroundColor: itemsForDay.length
                            ? "rgba(25, 118, 210, 0.08)"
                            : "inherit",
                          borderColor: isSelected ? "primary.main" : undefined,
                        }}
                        onClick={() => {
                          setSelectedDay(cell.startOf("day"));
                        }}
                      >
                        <Stack alignItems="center" spacing={0.5}>
                          <Typography variant="subtitle1">
                            {cell.date()}
                          </Typography>
                          {itemsForDay.length > 0 && (
                            <Chip
                              color="primary"
                              size="small"
                              label={`${itemsForDay.length} scheduled`}
                            />
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="h6">Monthly Overview</Typography>
                <Stack
                  flexWrap={{ xs: "wrap", lg: "nowrap" }}
                  useFlexGap
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  <Chip
                    color="primary"
                    label={`Total this month: ${totalForMonth}`}
                  />
                  {STATUS_OPTIONS.map((option) => (
                    <Chip
                      key={option.value}
                      label={`${option.label}: ${statusCounts[option.value] ?? 0}`}
                      color={option.value === "confirmed" ? "success" : "error"}
                    />
                  ))}
                </Stack>
              </Stack>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedDay
                  ? `Schedule for ${selectedDay.format("MMMM D, YYYY")}`
                  : "Select a day to view scheduled trucks"}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {selectedDay && selectedItems.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No trucks scheduled for this day.
                </Typography>
              )}
              {selectedDay && selectedItems.length > 0 && (
                <List>
                  {selectedItems.map((item) => {
                    const time = formatter.format(new Date(item.scheduledTime));
                    return (
                      <ListItem
                        key={item.id}
                        sx={{
                          alignItems: "flex-start",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle1">
                              {`${time} – Truck ${item.truckUnitNumber}`}
                            </Typography>
                            <Stack
                              flexWrap={{ xs: "wrap", lg: "nowrap" }}
                              useFlexGap
                              direction="row"
                              spacing={1}
                            >
                              <Chip
                                label={STATUS_LABEL_MAP[item.status]}
                                color={
                                  item.status === "confirmed"
                                    ? "success"
                                    : "error"
                                }
                                size="small"
                              />
                            </Stack>
                            {item.driverName && (
                              <Typography variant="body2" color="textSecondary">
                                Driver: {item.driverName}
                                {item.driverPhoneNumber
                                  ? ` • ${item.driverPhoneNumber}`
                                  : ""}
                              </Typography>
                            )}
                            {item.trailerUnitNumber && (
                              <Typography variant="body2" color="textSecondary">
                                Trailer: {item.trailerUnitNumber}
                              </Typography>
                            )}
                          </Stack>
                          <Stack
                            flexWrap={{ xs: "wrap", lg: "nowrap" }}
                            useFlexGap
                            direction="row"
                            spacing={1}
                          >
                            {item.status === "confirmed" &&
                              item.maintenanceOperationId && (
                                <CompleteMaintenanceOperationButton
                                  maintenanceOperationId={
                                    item.maintenanceOperationId
                                  }
                                  buttonLabel="Complete"
                                  buttonProps={{ size: "small" }}
                                  invalidateQueryKeys={[
                                    ["yardRecallSchedules"],
                                  ]}
                                />
                              )}
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenEditDialog(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => setScheduleToDelete(item)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      </ListItem>
                    );
                  })}
                </List>
              )}
              {!selectedDay && (
                <Typography variant="body2" color="textSecondary">
                  Use the calendar to choose a day and review the detailed
                  schedule.
                </Typography>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>
      <Box
        sx={{
          flexWrap: { xs: "wrap", lg: "nowrap" },
          mt: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <MaintenanceTable
          allowedOperations={["pmOilChange", "annualInspection", "yardRecall"]}
        />
      </Box>

      <CreateScheduleDialog
        open={createDialogOpen}
        truck={selectedTruck}
        onClose={() => {
          setCreateDialogOpen(false);
          setSelectedTruck(null);
        }}
        onSubmit={handleCreateSubmit}
        isSubmitting={createScheduleMutation.isPending}
        error={resolveErrorMessage(createScheduleMutation.error)}
      />

      <EditScheduleDialog
        open={editDialogOpen}
        schedule={scheduleToEdit}
        onClose={() => {
          setEditDialogOpen(false);
          setScheduleToEdit(null);
        }}
        onSubmit={handleUpdateSubmit}
        isSubmitting={updateScheduleMutation.isPending}
        error={resolveErrorMessage(updateScheduleMutation.error)}
      />

      <DeleteScheduleDialog
        schedule={scheduleToDelete}
        onClose={() => setScheduleToDelete(null)}
        onConfirm={() => {
          if (scheduleToDelete) {
            deleteScheduleMutation.mutate(scheduleToDelete.id);
          }
        }}
        isSubmitting={deleteScheduleMutation.isPending}
        error={resolveErrorMessage(deleteScheduleMutation.error)}
      />
    </Box>
  );
};

interface CreateScheduleDialogProps {
  open: boolean;
  truck: TruckListItem | null;
  onClose: () => void;
  onSubmit: (value: Dayjs | null) => void;
  isSubmitting: boolean;
  error?: string;
}

const CreateScheduleDialog = ({
  open,
  truck,
  onClose,
  onSubmit,
  isSubmitting,
  error,
}: CreateScheduleDialogProps) => {
  const [value, setValue] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (open) {
      setValue(dayjs().tz(CHICAGO_TIMEZONE).add(1, "hour").startOf("hour"));
    } else {
      setValue(null);
    }
  }, [open]);

  const handleSubmit = () => {
    onSubmit(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {truck ? `Schedule yard recall for ${truck.unitNumber}` : "Schedule"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Select the day and time for the yard recall. The status will be set
            to unconfirmed by default.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Scheduled time"
              value={value}
              onChange={(newValue) => setValue(newValue)}
              ampm
              format="DD MMM YYYY hh:mm A"
              timezone={CHICAGO_TIMEZONE}
              disablePast
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!value || isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface EditScheduleDialogProps {
  open: boolean;
  schedule: YardRecallScheduleViewDto | null;
  onClose: () => void;
  onSubmit: (value: Dayjs | null, status: YardRecallScheduleStatus) => void;
  isSubmitting: boolean;
  error?: string;
}

const EditScheduleDialog = ({
  open,
  schedule,
  onClose,
  onSubmit,
  isSubmitting,
  error,
}: EditScheduleDialogProps) => {
  const [value, setValue] = useState<Dayjs | null>(null);
  const [status, setStatus] = useState<YardRecallScheduleStatus>("unconfirmed");

  useEffect(() => {
    if (schedule) {
      setValue(dayjs.utc(schedule.scheduledTime).tz(CHICAGO_TIMEZONE));
      setStatus(schedule.status);
    } else if (!open) {
      setValue(null);
      setStatus("unconfirmed");
    }
  }, [schedule, open]);

  const handleSubmit = () => {
    onSubmit(value, status);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {schedule
          ? `Edit schedule for ${schedule.truckUnitNumber}`
          : "Edit schedule"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Scheduled time"
              value={value}
              onChange={(newValue) => setValue(newValue)}
              ampm
              format="DD MMM YYYY hh:mm A"
              timezone={CHICAGO_TIMEZONE}
              disablePast
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
          <FormControl fullWidth>
            <InputLabel id="yard-recall-status-label">Status</InputLabel>
            <Select
              labelId="yard-recall-status-label"
              label="Status"
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as YardRecallScheduleStatus)
              }
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!value || isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteScheduleDialogProps {
  schedule: YardRecallScheduleViewDto | null;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  error?: string;
}

const DeleteScheduleDialog = ({
  schedule,
  onClose,
  onConfirm,
  isSubmitting,
  error,
}: DeleteScheduleDialogProps) => (
  <Dialog open={Boolean(schedule)} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete schedule</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete the schedule for truck{" "}
        <strong>{schedule?.truckUnitNumber}</strong>?
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color="error"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Deleting..." : "Delete"}
      </Button>
    </DialogActions>
  </Dialog>
);

export default YardRecallSchedulePage;
